import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { FooterButtonContainer, StyledModal } from "../components/styled-modal";
import { Button } from "@interstate/components/Button";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary
} from "@interstate/components/AccordionGroup";
import { TemplateOptions } from "@coxauto-ui/cxm/communications/api";
import { TextInput } from "@interstate/components/TextInput";
import { InterstateOnChangeEvent } from "@interstate/components/InterstateEvents";
import { TextInputEventValue } from "@interstate/components/TextInput";
import { CenteredSpinner } from "@coxauto-ui/core/components";
import {
  templateModalCancelButtonTestId,
  templateModalContainerTestId,
  templateModalContentTestId,
  templateModalEmptyStateTestId,
  templateModalInsertButtonTestId,
  templateModalLoadingIndicatorTestId,
  templateModalSearchInputTestId
} from "../../testIds";
import { isReplyContentEmpty } from "../../utils/html-utils";

export interface BaseTemplateSelectorModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Function to close the modal */
  onClose: () => void;
  /** Template data from the API */
  templateData: TemplateOptions;
  /** Optional callback when a template is selected */
  onTemplateSelect?: (templateId: number, isTemplateGenAI: boolean) => void;
  /** Whether templates are currently being loaded */
  isLoading?: boolean;
  /** Whether a template is being inserted (loading template content) */
  isInsertingTemplate?: boolean;
  /** Reply text content for tracking if content is cleared */
  replyText: string;
  /** Optional subject text (for email) - if not provided, subject won't be checked */
  replyTextSubject?: string;
}

const ModalContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: min(800px, 70vh);
  overflow: hidden;
`;

const SearchContainer = styled.div`
  padding: 16px 24px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

const TemplateContent = styled.div`
  padding: 16px 24px;
`;

const TemplateItem = styled.div`
  padding: 8px 12px;
  margin: 4px 0;
  border: 1px solid transparent;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;

  &.selected {
    background-color: #cce8ff;
    border-color: #2196f3;
  }

  &:hover {
    background-color: #f4f4f4;
    border-color: #cccccc;
  }

  &.selected:hover {
    background-color: #e3f2fd;
    border-color: #318ad3;
  }
`;

const TemplateName = styled.div`
  font-weight: 500;
  color: #333333;
`;

const CategoryHeader = styled(AccordionSummary)`
  &:hover {
    background-color: transparent;
    cursor: default;
  }
`;

const EmptyState = styled.div`
  padding: 40px;
  text-align: center;
  color: #6d6d6d;
  font-style: italic;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  border-radius: 8px;
`;

/**
 * Base template selector modal component.
 * This provides shared logic and UI for both email and text template selectors.
 */
export function BaseTemplateSelectorModal({
  isOpen,
  onClose,
  templateData,
  onTemplateSelect,
  isLoading = false,
  isInsertingTemplate = false,
  replyText,
  replyTextSubject
}: BaseTemplateSelectorModalProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [selectedTemplateIsGenAI, setSelectedTemplateIsGenAI] =
    useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [insertedTemplate, setInsertedTemplate] = useState<number | null>(null);

  const handleSearchChange = (
    event: InterstateOnChangeEvent<TextInputEventValue>
  ) => {
    setSearchText(event.target.value);
  };

  // Filter template data based on search text
  const filteredTemplateData = useMemo(() => {
    if (!searchText.trim()) {
      return templateData;
    }

    const lowerSearchText = searchText.toLowerCase();
    const filtered: TemplateOptions = {};

    Object.entries(templateData).forEach(([category, templates]) => {
      // Check if category name matches search
      const categoryMatches = category.toLowerCase().includes(lowerSearchText);

      // Filter templates that match the search
      const matchingTemplates = templates.filter(
        template =>
          categoryMatches ||
          template.templateName.toLowerCase().includes(lowerSearchText)
      );

      // Only include category if it has matching templates
      if (matchingTemplates.length > 0) {
        filtered[category] = matchingTemplates;
      }
    });

    return filtered;
  }, [templateData, searchText]);

  const handleTemplateClick = (
    templateId: number,
    isGenAITemplate: boolean
  ) => {
    setSelectedTemplate(templateId);
    setSelectedTemplateIsGenAI(isGenAITemplate);
  };

  const handleTemplateDoubleClick = (
    templateId: number,
    isGenAITemplate: boolean
  ) => {
    handleTemplateClick(templateId, isGenAITemplate);
    handleInsert();
  };

  const handleInsert = () => {
    if (selectedTemplate !== null) {
      setSearchText("");
      setInsertedTemplate(selectedTemplate);
      onTemplateSelect?.(selectedTemplate, selectedTemplateIsGenAI);
    }
  };

  const handleClose = () => {
    if (!insertedTemplate) {
      setSelectedTemplate(null);
    }
    setSearchText("");
    onClose();
  };

  // Scroll to selected template's category when modal opens
  useEffect(() => {
    if (isOpen && insertedTemplate !== null) {
      setSelectedTemplate(insertedTemplate);

      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        const selectedElement = document.querySelector(
          `[data-template-id="${insertedTemplate}"]`
        );
        if (selectedElement) {
          selectedElement.scrollIntoView({
            behavior: "smooth",
            block: "center"
          });
        }
      }, 100);
    }
  }, [isOpen, insertedTemplate]);

  useEffect(() => {
    // If reply content is cleared, reset selected template
    // For text messages, replyTextSubject will be undefined
    if (isReplyContentEmpty(replyText, replyTextSubject || "")) {
      setInsertedTemplate(null);
      setSelectedTemplate(null);
    }
  }, [replyText, replyTextSubject]);

  const renderContent = () => {
    // Show loading spinner while templates are being fetched
    if (isLoading) {
      return (
        <ModalContentWrapper>
          <ScrollableContent>
            <CenteredSpinner id={templateModalLoadingIndicatorTestId} />
          </ScrollableContent>
        </ModalContentWrapper>
      );
    }

    const categoryEntries = Object.entries(filteredTemplateData);

    if (categoryEntries.length === 0) {
      const isSearching = searchText.trim().length > 0;
      return (
        <ModalContentWrapper>
          <SearchContainer>
            <TextInput
              clearButton
              autoComplete="off"
              data-testid={templateModalSearchInputTestId}
              id="template-modal-search-input"
              label="Search Templates"
              name="template-search"
              placeholder="Search by category or template name..."
              size="medium"
              value={searchText}
              onChange={handleSearchChange}
            />
          </SearchContainer>
          <ScrollableContent>
            <EmptyState data-testid={templateModalEmptyStateTestId}>
              {isSearching
                ? "No templates found matching your search"
                : "No templates available"}
            </EmptyState>
          </ScrollableContent>
        </ModalContentWrapper>
      );
    }

    const getCategoryTestId = (category: string) => {
      return `template-category-${category.replace(/\s+/g, "-").toLowerCase()}`;
    };

    return (
      <ModalContentWrapper>
        <SearchContainer>
          <TextInput
            clearButton
            autoComplete="off"
            data-testid={templateModalSearchInputTestId}
            id="template-modal-search-input"
            label="Search Templates"
            name="template-search"
            placeholder="Search by category or template name..."
            size="medium"
            value={searchText}
            onChange={handleSearchChange}
          />
        </SearchContainer>
        <ScrollableContent>
          <TemplateContent data-testid={templateModalContentTestId}>
            {categoryEntries.map(([category, templates]) => (
              <Accordion
                defaultExpanded
                data-testid={getCategoryTestId(category)}
                key={category}
              >
                <CategoryHeader data-testid={getCategoryTestId(category)}>
                  {category} ({templates.length})
                </CategoryHeader>
                <AccordionDetails data-testid={getCategoryTestId(category)}>
                  {templates.map(template => (
                    <TemplateItem
                      className={
                        selectedTemplate === template.templateId
                          ? "selected"
                          : ""
                      }
                      data-template-id={template.templateId}
                      data-testid={`template-item-${template.templateId}`}
                      key={template.templateId}
                      onClick={() =>
                        handleTemplateClick(
                          template.templateId,
                          template.isGenAITemplate
                        )
                      }
                      onDoubleClick={() =>
                        handleTemplateDoubleClick(
                          template.templateId,
                          template.isGenAITemplate
                        )
                      }
                    >
                      <TemplateName
                        data-testid={`template-name-${template.templateId}`}
                      >
                        {template.templateName}
                      </TemplateName>
                    </TemplateItem>
                  ))}
                </AccordionDetails>
              </Accordion>
            ))}
          </TemplateContent>
        </ScrollableContent>
      </ModalContentWrapper>
    );
  };

  const modalContent = renderContent();

  const modalFooter = (
    <FooterButtonContainer>
      <Button
        buttonStyle="secondary"
        data-testid={templateModalCancelButtonTestId}
        disabled={isInsertingTemplate}
        onClick={handleClose}
      >
        Cancel
      </Button>
      <Button
        buttonStyle="primary"
        data-testid={templateModalInsertButtonTestId}
        disabled={selectedTemplate === null || isInsertingTemplate}
        onClick={handleInsert}
      >
        {isInsertingTemplate ? "Inserting..." : "Insert"}
      </Button>
    </FooterButtonContainer>
  );

  return (
    <StyledModal
      containerTestId={templateModalContainerTestId}
      footer={modalFooter}
      isOpen={isOpen}
      title="Templates"
      onClose={handleClose}
    >
      {isInsertingTemplate && (
        <LoadingOverlay>
          <CenteredSpinner id="template-insert-loading-spinner" />
        </LoadingOverlay>
      )}
      {modalContent}
    </StyledModal>
  );
}

export default BaseTemplateSelectorModal;
